import {
	createBlock,
	cloneBlock,
	createBlocksFromInnerBlocksTemplate,
} from '@wordpress/blocks';
import { LAYOUT_PRESETS } from './constants';

export const REGION_BLOCK = 'brandy/tab-region';

/**
 * The area sequence a tab currently holds, as a comparable string.
 *
 * `#` stands for anything that is not a region, so free-form content and a
 * region set are never mistaken for one another.
 *
 * @param {Object} tab       A `brandy/tab` block object.
 * @param {string} presetKey Layout preset key.
 * @return {string} Signature for the tab's current shape under this preset.
 */
export function tabSignature( tab, presetKey ) {
	return (
		presetKey +
		'|' +
		( tab.innerBlocks || [] )
			.map( ( block ) =>
				REGION_BLOCK === block.name ? block.attributes.area : '#'
			)
			.join( ',' )
	);
}

/**
 * A tab's content as plain blocks, with any regions unwrapped.
 *
 * Regions are concatenated in the order they appear, which is the order the
 * preset declared them — so merging back to a single stream preserves reading
 * order rather than interleaving by position on screen. Blocks sitting loose
 * beside a region (possible if content was edited while a preset was mid-swap)
 * are kept in place rather than dropped.
 *
 * @param {Object} tab A `brandy/tab` block object.
 * @return {Array} Content blocks, no regions.
 */
export function flattenTabContent( tab ) {
	const children = tab.innerBlocks || [];

	if ( ! children.some( ( block ) => REGION_BLOCK === block.name ) ) {
		return children;
	}

	return children.flatMap( ( block ) =>
		REGION_BLOCK === block.name ? block.innerBlocks : [ block ]
	);
}

/**
 * The children a tab should have under `presetKey`.
 *
 * Returns `null` when the tab already matches, so the caller can skip the
 * dispatch — this runs from an effect that re-fires on every inner block
 * change, and replacing blocks unconditionally would loop.
 *
 * @param {Object} tab       A `brandy/tab` block object.
 * @param {string} presetKey Layout preset key.
 * @return {Array|null} Replacement children, or null if already correct.
 */
export function buildTabChildren( tab, presetKey ) {
	const preset = LAYOUT_PRESETS[ presetKey ] || LAYOUT_PRESETS.classic;
	const children = tab.innerBlocks || [];
	const areas = children.map( ( block ) =>
		REGION_BLOCK === block.name ? block.attributes.area : null
	);

	// Classic: no regions. Unwrap back to a single content stream, which is
	// also the shape every tab saved before presets existed.
	if ( ! preset.regions.length ) {
		if ( ! areas.some( Boolean ) ) {
			return null;
		}

		return flattenTabContent( tab ).map( cloneBlock );
	}

	/*
	 * Already the right regions in the right order, so nothing to do — which
	 * also keeps each region's content in place when switching between two
	 * presets that share a region set (side-left <-> side-right).
	 *
	 * Only the areas are compared. Labels were deliberately left out: they are
	 * cosmetic, and reconciling them meant rebuilding the regions whenever they
	 * differed. That branch could never settle if a rebuilt block came back
	 * with anything other than the label asked for, and an effect that rebuilds
	 * on every pass is what takes the editor down with "Maximum update depth
	 * exceeded". A preset that renames its areas is a migration, not something
	 * to reconcile on every render.
	 */
	const wanted = preset.regions.map( ( region ) => region.area );
	const sameShape =
		areas.length === wanted.length &&
		areas.every( ( area, i ) => area === wanted[ i ] );

	if ( sameShape ) {
		return null;
	}

	/*
	 * Entering a regioned preset (or repairing a tab that has none, which is
	 * what a freshly inserted tab looks like). Everything the tab holds goes to
	 * the first region; the rest are filled from the template the preset gives
	 * them, so a side area never arrives as an unexplained empty column.
	 *
	 * The moved blocks are cloned rather than re-parented by reference.
	 * `replaceInnerBlocks` given blocks that are still descendants of the ones
	 * being replaced can leave the same clientId in the tree twice, and a tree
	 * that never settles re-renders forever. Templated regions are built fresh
	 * each time, so they carry no borrowed clientIds either.
	 */
	const content = flattenTabContent( tab ).map( cloneBlock );

	return preset.regions.map( ( region, i ) =>
		createBlock(
			REGION_BLOCK,
			{ area: region.area, label: region.label },
			0 === i
				? content
				: createBlocksFromInnerBlocksTemplate( region.template || [] )
		)
	);
}

/**
 * Bring every tab's children into line with the active preset.
 *
 * `applied` is the backstop against a runaway. It records the shape each tab
 * was last acted on, so the same input can never be dispatched twice: if a
 * replacement somehow does not produce the shape that was asked for, this
 * settles for a wrong layout rather than dispatching forever and taking the
 * editor down. Pass a Map that outlives renders (a ref) for it to do anything.
 *
 * @param {Array}    tabs               `brandy/tab` block objects.
 * @param {string}   presetKey          Layout preset key.
 * @param {Function} replaceInnerBlocks blockEditor store dispatcher.
 * @param {Map}      [applied]          clientId -> last signature acted on.
 */
export function applyLayoutPreset(
	tabs,
	presetKey,
	replaceInnerBlocks,
	applied
) {
	tabs.forEach( ( tab ) => {
		const signature = tabSignature( tab, presetKey );

		if ( applied && applied.get( tab.clientId ) === signature ) {
			return;
		}

		const next = buildTabChildren( tab, presetKey );

		if ( applied ) {
			applied.set( tab.clientId, signature );
		}

		if ( null !== next ) {
			replaceInnerBlocks( tab.clientId, next, false );
		}
	} );
}
